import type { Translate } from 'next-translate';
import React from 'react';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as TSwiper } from 'swiper/types';
import ArrowIcon from '@/public/icons/arrow-slider.svg';
import { PRICING_TESTIMONIALS } from '../../data';
import styles from './PricingTestimonials.module.scss';

const STARS = 5;

interface Props {
	t: Translate;
}

export const PricingTestimonials: React.FC<Props> = ({ t }) => {
	const swiperRef = React.useRef<TSwiper | null>(null);
	const [navigation, setNavigation] = React.useState({
		isBeginning: true,
		isEnd: false,
	});

	const onPrev = () => swiperRef.current?.slidePrev();
	const onNext = () => swiperRef.current?.slideNext();

	return (
		<section className={styles['slider']}>
			<div className={styles['slider__header']}>
				<h2 className={styles['slider__title']}>{t('testimonials_title')}</h2>

				<div className={styles['slider__navigation']}>
					<button
						type='button'
						className={styles['slider__nav-button']}
						onClick={onPrev}
						disabled={navigation.isBeginning}
						aria-label={t('testimonials_prev')}
					>
						<ArrowIcon
							className={styles['slider__nav-icon']}
							aria-hidden='true'
							focusable='false'
						/>
					</button>
					<button
						type='button'
						className={styles['slider__nav-button']}
						onClick={onNext}
						disabled={navigation.isEnd}
						aria-label={t('testimonials_next')}
					>
						<ArrowIcon
							className={`${styles['slider__nav-icon']} ${styles['slider__nav-icon_next']}`}
							aria-hidden='true'
							focusable='false'
						/>
					</button>
				</div>
			</div>

			<Swiper
				grabCursor
				allowTouchMove
				spaceBetween={24}
				slidesPerView={1.25}
				onInit={swiper => {
					swiperRef.current = swiper;
					setNavigation({
						isBeginning: swiper.isBeginning,
						isEnd: swiper.isEnd,
					});
				}}
				onSlideChange={swiper => {
					setNavigation({
						isBeginning: swiper.isBeginning,
						isEnd: swiper.isEnd,
					});
				}}
				breakpoints={{
					768: { slidesPerView: 2.25 },
					1024: { slidesPerView: 3, grabCursor: false, allowTouchMove: false },
				}}
				className={styles['slider__swiper']}
			>
				{PRICING_TESTIMONIALS.map(review => (
					<SwiperSlide key={review.id} className={styles['slide']}>
						<div className={styles['slide__box']}>
							<div className={styles['slide__top']}>
								<div className={styles['slide__header']}>
									<div
										className={styles['slide__rating']}
										aria-label={`${STARS} out of ${STARS}`}
									>
										{Array.from({ length: STARS }, (_, index) => (
											<span key={index} className={styles['slide__star']}>
												⭐️
											</span>
										))}
									</div>
									<a
										href={review.link}
										className={styles['slide__link']}
										target='_blank'
										rel='noreferrer'
									>
										{t('testimonials_link')}
									</a>
								</div>
								<span className={styles['slide__date']}>{review.date}</span>
								<p className={styles['slide__text']}>{review.text}</p>
							</div>
							<b className={styles['slide__name']}>{review.author}</b>
						</div>
					</SwiperSlide>
				))}
			</Swiper>
		</section>
	);
};
